'use client'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { CalendarClock, Copy } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

interface VersionInfo {
  version: string
  sha: string
  builtAt: string
}

interface AppVersionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const AppVersionDialog = ({
  open,
  onOpenChange,
}: AppVersionDialogProps) => {
  const [info, setInfo] = useState<VersionInfo | null>(null)
  const [copying, setCopying] = useState(false)
  const [serverIp, setServerIp] = useState<string | null>(null)

  useEffect(() => {
    fetch('/version.json')
      .then((res) => res.json())
      .then(setInfo)
      .catch(() => setInfo(null))

    fetch('/api/server-info')
      .then((res) => res.json())
      .then((data) => setServerIp(data.ips?.[0] ?? null))
      .catch(() => setServerIp(null))
  }, [])

  // Hàm copy thông tin version/sha vào clipboard
  const handleCopy = async () => {
    if (!info) return
    setCopying(true)
    try {
      // Copy định dạng: vX.Y.Z #abcdefg (2024-06-01 12:34:56)
      const text = `${info.version} #${info.sha.slice(0, 7)} (${new Date(info.builtAt).toLocaleString()})`
      await navigator.clipboard.writeText(text)
      toast.success('Đã copy thông tin phiên bản!')
    } catch {
      toast.error('Copy thất bại')
    } finally {
      setCopying(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side='bottom' className='h-fit p-2'>
        <SheetHeader>
          <SheetTitle className='flex items-center justify-center gap-2 text-center text-base'>
            Phiên bản:{' '}
            <span className='font-semibold uppercase text-primary'>
              {info?.version ?? '...'}
            </span>
            {/* Nút copy cạnh tag version */}
            {info && (
              <Button
                type='button'
                size='icon'
                variant='ghost'
                className='ml-1 h-6 w-6 p-0'
                aria-label='Copy version info'
                onClick={handleCopy}
                disabled={copying}
                tabIndex={0}
              >
                <Copy className='h-4 w-4 text-muted-foreground' />
              </Button>
            )}
          </SheetTitle>
        </SheetHeader>
        {info && (
          <div className='flex items-center justify-center gap-1.5 py-1 text-center text-sm text-muted-foreground'>
            <span className='font-semibold text-primary'>{info.version}</span>
            <span className='text-muted-foreground'>
              #{info.sha.slice(0, 7)}
            </span>
            <span>•</span>
            <CalendarClock className='h-2.5 w-2.5' />
            <span className='text-muted-foreground'>
              {new Date(info.builtAt).toLocaleString()}
            </span>
            <span>•</span>
            WEB: {serverIp ?? '...'}
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
